/**
 * 電子發票面板（平台先用）：設定 / 字軌 / 發票維護三個子分頁。
 * 嵌在帳單管理頁（PlatformBillingPage）的「電子發票」分頁下，沿用 ee_platform_billing 權限。
 *
 * - 設定：開關 / 環境 / 賣方資料 / 憑證上傳（.pfx/.p12·機密只送不收）/ 傳送帳號（機密）。
 * - 字軌：配號區間列表 + 新增（剩餘量 < 10% 以紅字警示）。
 * - 發票維護：依年月 / 狀態列出；作廢（跨期別暫擋·待 porsche 決定）、提供發票資訊再寄送。
 *
 * 訊息一律走 useMessage（SYS/ERR 代碼）；機密欄位不回填、不顯示明文。
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, Divider, FormControlLabel, MenuItem, Paper, Stack, Switch, Tab, Table,
  TableBody, TableCell, TableHead, TableRow, Tabs, TextField, Typography,
} from '@mui/material';

import { useMessage } from '../../../contexts/MessageContext';
import * as svc from '../../../services/einvoiceService';

const ENV_OPTIONS: svc.EInvoiceEnv[] = ['test', 'prod'];

function fmt(n: number): string {
  return (n ?? 0).toLocaleString('zh-TW');
}

const UPLOAD_LABEL: Record<svc.UploadStatus, { label: string; color: 'default' | 'success' | 'error' | 'warning' }> = {
  pending: { label: '待上傳', color: 'warning' },
  uploaded: { label: '已上傳', color: 'success' },
  failed: { label: '上傳失敗', color: 'error' },
};

const EInvoicePanel: React.FC = () => {
  const { showSuccess, showError, showApiError } = useMessage();
  const [tab, setTab] = useState(0);

  return (
    <Box>
      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="設定" />
        <Tab label="字軌" />
        <Tab label="發票維護" />
      </Tabs>
      {tab === 0 && <SettingsTab {...{ showSuccess, showError, showApiError }} />}
      {tab === 1 && <TracksTab {...{ showSuccess, showApiError }} />}
      {tab === 2 && <MaintainTab {...{ showSuccess, showApiError }} />}
    </Box>
  );
};

// ─────────────────────────── 設定 ───────────────────────────

type MsgFns = {
  showSuccess: (c: string, p?: Record<string, string>) => void;
  showError: (c: string, p?: Record<string, string>) => void;
  showApiError: (e: unknown) => void;
};

const SettingsTab: React.FC<MsgFns> = ({ showSuccess, showError, showApiError }) => {
  const [cfg, setCfg] = useState<svc.EInvoiceConfig | null>(null);
  const [form, setForm] = useState<svc.EInvoiceConfigUpdate>({});
  const [transmit, setTransmit] = useState('');
  const [busy, setBusy] = useState(false);
  // 憑證上傳（S3：主體 / 到期日由後端自動解析，不再人工輸入）
  const [certFile, setCertFile] = useState<File | null>(null);
  const [certPwd, setCertPwd] = useState('');

  const load = useCallback(async () => {
    try {
      const c = await svc.getConfig();
      setCfg(c);
      setForm({
        enabled: c.enabled, environment: c.environment,
        seller_tax_id: c.seller_tax_id || '', seller_name: c.seller_name || '',
        seller_address: c.seller_address || '', routing_code: c.routing_code || '',
      });
    } catch (err) { showApiError(err); }
  }, [showApiError]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const payload: svc.EInvoiceConfigUpdate = { ...form };
      if (transmit) payload.transmit_account = transmit;
      const c = await svc.saveConfig(payload);
      setCfg(c);
      setTransmit('');
      showSuccess('SYS450011');
    } catch (err) { showApiError(err); } finally { setBusy(false); }
  };

  const upload = async () => {
    if (!certFile) { showError('ERR020006', { field: '憑證', detail: '請選擇憑證檔' }); return; }
    setBusy(true);
    try {
      const c = await svc.uploadCertificate(certFile, certPwd);
      setCfg(c);
      setCertFile(null); setCertPwd('');
      showSuccess('SYS450012', { expiry: c.cert_expire_at || '—' });
    } catch (err) { showApiError(err); } finally { setBusy(false); }
  };

  const set = (k: keyof svc.EInvoiceConfigUpdate) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Stack spacing={2} sx={{ maxWidth: 680 }}>
      {cfg && !cfg.complete && (
        <Alert severity="info">尚未完成設定（需統編 + 憑證 + 傳送帳號）才能開立電子發票。</Alert>
      )}
      <FormControlLabel
        control={<Switch checked={!!form.enabled}
          onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))} />}
        label="啟用電子發票（關閉時帳單維持現狀，發票號碼仍可人工填）" />
      <TextField select label="環境" value={form.environment || 'test'}
        onChange={set('environment')} size="small">
        {ENV_OPTIONS.map((e) => (
          <MenuItem key={e} value={e}>{e === 'test' ? '財政部測試平台' : '正式'}</MenuItem>
        ))}
      </TextField>
      <TextField label="賣方統一編號" value={form.seller_tax_id || ''} onChange={set('seller_tax_id')}
        size="small" inputProps={{ maxLength: 8 }} />
      <TextField label="賣方名稱" value={form.seller_name || ''} onChange={set('seller_name')} size="small" />
      <TextField label="賣方地址" value={form.seller_address || ''} onChange={set('seller_address')} size="small" />
      <TextField label="繞送代碼" value={form.routing_code || ''} onChange={set('routing_code')} size="small" />
      <TextField label="Turnkey 傳送帳號（機密·留空不更動）" value={transmit} type="password"
        onChange={(e) => setTransmit(e.target.value)} size="small"
        placeholder={cfg?.has_transmit_account ? '已設定（如需更換再輸入）' : '尚未設定'} />
      <Box>
        <Button variant="contained" onClick={save} disabled={busy}>儲存設定</Button>
      </Box>

      <Divider />
      <Typography variant="subtitle2">憑證（軟體憑證 .pfx / .p12）</Typography>
      {cfg?.has_cert ? (
        <Alert severity="success">
          已上傳憑證{cfg.cert_subject ? `（${cfg.cert_subject}）` : ''}
          {cfg.cert_expire_at ? `·到期日 ${cfg.cert_expire_at}` : ''}
        </Alert>
      ) : (
        <Alert severity="warning">尚未上傳憑證</Alert>
      )}
      <Button variant="outlined" component="label" size="small">
        {certFile ? certFile.name : '選擇憑證檔'}
        <input hidden type="file" accept=".pfx,.p12"
          onChange={(e) => setCertFile(e.target.files?.[0] || null)} />
      </Button>
      <TextField label="憑證密碼" type="password" value={certPwd}
        onChange={(e) => setCertPwd(e.target.value)} size="small" />
      <Typography variant="caption" color="text.secondary">
        上傳後系統會驗證密碼並自動讀取憑證主體與到期日（到期前 30 天提醒）。檔案上限 64 KB。
      </Typography>
      <Box>
        <Button variant="contained" onClick={upload} disabled={busy || !certFile}>上傳憑證</Button>
      </Box>
    </Stack>
  );
};

// ─────────────────────────── 字軌 ───────────────────────────

const TracksTab: React.FC<Pick<MsgFns, 'showSuccess' | 'showApiError'>> = ({ showSuccess, showApiError }) => {
  const [env, setEnv] = useState<svc.EInvoiceEnv>('test');
  const [rows, setRows] = useState<svc.Track[]>([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState<svc.TrackCreate>({
    period: '', track: '', environment: 'test', begin_no: 0, end_no: 0,
  });

  const load = useCallback(async () => {
    try { setRows(await svc.listTracks(env)); } catch (err) { showApiError(err); }
  }, [env, showApiError]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    setBusy(true);
    try {
      await svc.addTrack({ ...form, environment: env,
        begin_no: Number(form.begin_no), end_no: Number(form.end_no) });
      showSuccess('SYS450013', { track: form.track, period: form.period });
      setForm({ period: '', track: '', environment: env, begin_no: 0, end_no: 0 });
      load();
    } catch (err) { showApiError(err); } finally { setBusy(false); }
  };

  const remaining = (t: svc.Track) => Math.max(0, t.end_no - t.next_no + 1);
  const total = (t: svc.Track) => Math.max(0, t.end_no - t.begin_no + 1);
  const low = (t: svc.Track) => total(t) > 0 && remaining(t) <= total(t) * 0.1;

  return (
    <Stack spacing={2}>
      <TextField select label="環境" value={env} size="small" sx={{ maxWidth: 220 }}
        onChange={(e) => setEnv(e.target.value as svc.EInvoiceEnv)}>
        {ENV_OPTIONS.map((e) => (
          <MenuItem key={e} value={e}>{e === 'test' ? '財政部測試平台' : '正式'}</MenuItem>
        ))}
      </TextField>

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>期別</TableCell><TableCell>字軌</TableCell>
              <TableCell align="right">起號</TableCell><TableCell align="right">迄號</TableCell>
              <TableCell align="right">下一號</TableCell><TableCell align="right">剩餘</TableCell>
              <TableCell>狀態</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((t) => (
              <TableRow key={t.id}>
                <TableCell>{t.period}</TableCell>
                <TableCell>{t.track}</TableCell>
                <TableCell align="right">{fmt(t.begin_no)}</TableCell>
                <TableCell align="right">{fmt(t.end_no)}</TableCell>
                <TableCell align="right">{fmt(t.next_no)}</TableCell>
                <TableCell align="right" sx={{ color: low(t) ? 'error.main' : undefined }}>
                  {fmt(remaining(t))}{low(t) ? '（偏低）' : ''}
                </TableCell>
                <TableCell>{t.is_active ? '啟用' : '停用'}</TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={7} align="center">尚無字軌</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Typography variant="subtitle2">新增配號區間（國稅局配號）</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <TextField label="期別 YYYYMM" value={form.period} size="small"
          onChange={(e) => setForm((f) => ({ ...f, period: e.target.value }))}
          inputProps={{ maxLength: 6 }} />
        <TextField label="字軌（2 碼）" value={form.track} size="small"
          onChange={(e) => setForm((f) => ({ ...f, track: e.target.value.toUpperCase() }))}
          inputProps={{ maxLength: 2 }} />
        <TextField label="起號" type="number" value={form.begin_no} size="small"
          onChange={(e) => setForm((f) => ({ ...f, begin_no: Number(e.target.value) }))} />
        <TextField label="迄號" type="number" value={form.end_no} size="small"
          onChange={(e) => setForm((f) => ({ ...f, end_no: Number(e.target.value) }))} />
        <Button variant="contained" onClick={add} disabled={busy}>新增</Button>
      </Stack>
    </Stack>
  );
};

// ─────────────────────────── 發票維護 ───────────────────────────

const MaintainTab: React.FC<Pick<MsgFns, 'showSuccess' | 'showApiError'>> = ({ showSuccess, showApiError }) => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [status, setStatus] = useState<svc.InvoiceStatus | ''>('');
  const [rows, setRows] = useState<svc.IssuedInvoice[]>([]);
  const [busy, setBusy] = useState(false);
  const [voidTarget, setVoidTarget] = useState<svc.IssuedInvoice | null>(null);
  const [voidReason, setVoidReason] = useState('');

  const load = useCallback(async () => {
    try {
      setRows(await svc.listInvoices({ year, month, status: status || undefined }));
    } catch (err) { showApiError(err); }
  }, [year, month, status, showApiError]);

  useEffect(() => { load(); }, [load]);

  const doVoid = async () => {
    if (!voidTarget) return;
    setBusy(true);
    try {
      await svc.voidInvoice(voidTarget.id, voidReason);
      showSuccess('SYS450010', { invoice_no: voidTarget.invoice_no });
      setVoidTarget(null); setVoidReason('');
      load();
    } catch (err) { showApiError(err); } finally { setBusy(false); }
  };

  const resend = async (inv: svc.IssuedInvoice) => {
    setBusy(true);
    try {
      await svc.resendInvoice(inv.id);
      showSuccess('SYS450014', { invoice_no: inv.invoice_no });
    } catch (err) { showApiError(err); } finally { setBusy(false); }
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField label="年" type="number" value={year} size="small" sx={{ width: 110 }}
          onChange={(e) => setYear(Number(e.target.value))} />
        <TextField label="月" type="number" value={month} size="small" sx={{ width: 90 }}
          onChange={(e) => setMonth(Number(e.target.value))} inputProps={{ min: 1, max: 12 }} />
        <TextField select label="狀態" value={status} size="small" sx={{ width: 140 }}
          onChange={(e) => setStatus(e.target.value as svc.InvoiceStatus | '')}>
          <MenuItem value="">全部</MenuItem>
          <MenuItem value="issued">已開立</MenuItem>
          <MenuItem value="void">已作廢</MenuItem>
        </TextField>
        <Button variant="outlined" onClick={load} disabled={busy}>查詢</Button>
      </Stack>

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>發票號碼</TableCell><TableCell>日期</TableCell><TableCell>買方</TableCell>
              <TableCell align="right">未稅</TableCell><TableCell align="right">稅</TableCell>
              <TableCell align="right">總計</TableCell><TableCell>狀態</TableCell>
              <TableCell>上傳</TableCell><TableCell align="right">動作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((inv) => (
              <TableRow key={inv.id}>
                <TableCell>{inv.invoice_no}</TableCell>
                <TableCell>{inv.invoice_date}</TableCell>
                <TableCell>{inv.buyer_title}（{inv.buyer_tax_id}）</TableCell>
                <TableCell align="right">{fmt(inv.sales_amount)}</TableCell>
                <TableCell align="right">{fmt(inv.tax_amount)}</TableCell>
                <TableCell align="right">{fmt(inv.total_amount)}</TableCell>
                <TableCell>
                  {inv.status === 'void'
                    ? <Chip size="small" color="default" label="已作廢" />
                    : <Chip size="small" color="primary" label="已開立" />}
                </TableCell>
                <TableCell>
                  <Chip size="small" color={UPLOAD_LABEL[inv.upload_status].color}
                    label={UPLOAD_LABEL[inv.upload_status].label}
                    title={inv.upload_error || undefined} />
                </TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={() => resend(inv)} disabled={busy}>再寄送</Button>
                  {inv.status === 'issued' && (
                    <Button size="small" color="error" disabled={busy}
                      onClick={() => { setVoidTarget(inv); setVoidReason(''); }}>作廢</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={9} align="center">查無發票</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={!!voidTarget} onClose={() => setVoidTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>作廢發票 {voidTarget?.invoice_no}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>
            作廢後將產生作廢存證並上傳，另掛一份「已作廢」證明聯（原件保留），請填寫作廢原因。
            跨期別發票目前暫不開放作廢（待法規確認）。
          </DialogContentText>
          <TextField autoFocus fullWidth label="作廢原因" value={voidReason}
            onChange={(e) => setVoidReason(e.target.value)} size="small" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoidTarget(null)}>取消</Button>
          <Button color="error" variant="contained" onClick={doVoid}
            disabled={busy || !voidReason.trim()}>確定作廢</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default EInvoicePanel;
